import { CloseOutlined } from '@ant-design/icons';
import { Col, InputNumber, Row, Table } from 'antd';
import TextArea from 'antd/lib/input/TextArea';
import { ColumnType } from 'antd/lib/table';
import { FC } from 'react';
import { useDebouncedCallback } from 'use-debounce';

import { Reup_Product } from '@/graphql/types';
import { LineItem, PurchaseOrder } from '@/types';
import {
  HandleAutoCompleteDraftOrderCallback,
  HandleLineItemQuantityUpdated,
  HandleLineItemRemove,
  HandleNoteUpdate,
  HandleSelectedOrdersChange,
} from '@/types/draftOrderTypes';

import formatCurrency from '../../common/Extensions/CurrencyExtensions';
import AutoCompleteDraftOrderProduct from './autoCompleteDraftOrderProduct';

const defaultRowExpandThreshold = 2;

// ToDo: This will be replaced with a call to GraphQl

type DraftOrdersTableProps = {
  purchaseOrders: PurchaseOrder[];
  onLineItemRemoved: HandleLineItemRemove;
  onLineItemQuantityUpdated: HandleLineItemQuantityUpdated;
  onSelectedOrdersChange: HandleSelectedOrdersChange;
  products: Reup_Product[];
  onAutoCompleteSelect: HandleAutoCompleteDraftOrderCallback;
  onNoteUpdate: HandleNoteUpdate;
};

const DraftOrdersTable: FC<DraftOrdersTableProps> = ({
  purchaseOrders,
  onLineItemRemoved,
  onLineItemQuantityUpdated,
  onSelectedOrdersChange,
  onAutoCompleteSelect,
  onNoteUpdate,
  products,
}) => {
  const rowSelection = {
    onChange: (selectedRowKeys: React.Key[], selectedRows: PurchaseOrder[]) => {
      onSelectedOrdersChange(selectedRows);
    },
  };

  const purchaseOrderColumns: ColumnType<PurchaseOrder>[] = [
    {
      title: 'Supplier',
      dataIndex: 'SupplierName',
      key: 'supplierName',
      render: (text: string, record: PurchaseOrder) =>
        `${text}-${record.SupplierId}`,
    },
    {
      title: 'Items',
      dataIndex: 'LineItems',
      key: 'lineItems',
      render: (record: LineItem[]) => record.length,
    },
    {
      title: 'Total',
      dataIndex: 'LineItems',
      key: 'totalPrice',
      render: (record: LineItem[]) => {
        const sumAll = record
          .map((item) =>
            item.TotalPrice && item.TotalPrice > 0 ? item.TotalPrice : 0
          )
          .reduce((prev, curr) => prev + curr, 0);

        return formatCurrency(sumAll);
      },
    },
  ];

  // auto-expand rows
  const expandRows = purchaseOrders.length <= defaultRowExpandThreshold;

  type NoteUpdate = {
    poId: string;
    note: string;
  };

  const debounced = useDebouncedCallback((value: NoteUpdate) => {
    onNoteUpdate(value.poId, value.note);
  }, 2500);

  const footer = (supplierId: string, purchaseOrderId: string) => {
    const po = purchaseOrders.find((p) => p.Id === purchaseOrderId);

    // Only Products for Supplier
    // Products not already in the PO
    const supplierProducts = products.filter(
      (p) =>
        p.Suppliers?.every((s) => s.SupplierId === supplierId) &&
        !po?.LineItems.map((li) => li.Product?.key).includes(p.key)
    );

    return (
      <Row justify="space-between" gutter={[24, 24]}>
        <Col>
          <h4>Add Items</h4>
          <AutoCompleteDraftOrderProduct
            key={`${supplierId}-autoComplete`}
            products={supplierProducts}
            autoCompleteValue=""
            purchaseOrderId={purchaseOrderId}
            onAutoCompleteSelect={onAutoCompleteSelect}
          />
        </Col>
        <Col flex="auto">
          <h4>Notes</h4>
          <TextArea
            style={{ maxWidth: '545px' }}
            rows={4}
            placeholder="Notes"
            defaultValue={po?.Note}
            onChange={(e) =>
              debounced({ poId: purchaseOrderId, note: e.target.value ?? '' })
            }
            showCount
            maxLength={100}
          />
        </Col>
      </Row>
    );
  };

  const lineItemTable = (lineItems: LineItem[], supplierId: string) => {
    const lineItemColumns = [
      {
        title: 'Product Name',
        dataIndex: ['Product', 'ProductName'],
      },
      {
        title: 'Product Category',
        dataIndex: ['Product', 'CategoryType'],
        key: `${supplierId}-ProductCategory`,
      },
      {
        title: 'On Hand',
        dataIndex: ['Product', 'QuantityOnHand'],
        key: `${supplierId}-ProductQuantityOnHand`,
      },
      {
        title: 'Cost',
        dataIndex: 'UnitPrice',
        key: `${supplierId}-UnitPrice`,
        render: (record: number) => {
          return formatCurrency(record);
        },
      },
      {
        title: 'Quantity',
        dataIndex: 'RequestedQuantity',
        key: `${supplierId}-RequestedQuantity`,
        render: (qty: number, record: LineItem) => {
          return (
            <InputNumber
              defaultValue={qty}
              onChange={(value: number) => {
                record.RequestedQuantity = value;
                onLineItemQuantityUpdated(record);
              }}
            />
          );
        },
      },
      {
        title: 'Total',
        key: `${supplierId}-TotalPrice`,
        render: (record: LineItem) => {
          if (record?.UnitPrice)
            return formatCurrency(record.RequestedQuantity * record.UnitPrice);
          return 0;
        },
      },
      {
        title: 'Remove',
        key: `${supplierId}-Remove`,
        render: (record: LineItem) => {
          return (
            <CloseOutlined
              onClick={() => {
                const order = purchaseOrders.find(
                  (p) => p.Id === record.PurchaseOrderId
                );
                if (order) onLineItemRemoved(record, order);
              }}
            />
          );
        },
      },
    ];
    return (
      <Table
        rowKey="Id"
        key={`${supplierId}-lineItems`}
        columns={lineItemColumns}
        dataSource={lineItems}
        pagination={false}
        footer={() => footer(supplierId, lineItems[0]?.PurchaseOrderId)}
        className="p-[0px_49px_49px_0px]"
      />
    );
  };

  return (
    <Row>
      <Col span={24}>
        <Table
          rowKey="Id"
          columns={purchaseOrderColumns}
          expandable={{
            expandedRowRender: (record: PurchaseOrder) => {
              return lineItemTable(record.LineItems, record.SupplierId);
            },
            defaultExpandAllRows: expandRows,
          }}
          dataSource={purchaseOrders}
          rowSelection={{
            type: 'checkbox',
            ...rowSelection,
          }}
        />
      </Col>
    </Row>
  );
};

export default DraftOrdersTable;
